import Button from '@raydiant/raydial/components/Button';
import {
  Footer,
  Header,
  SiteUidTextFields,
  QsrProgressStepperContainer,
  Site,
} from './components';
import Text from '@raydiant/raydial/components/Text';
import Link from '@raydiant/raydial/components/Link';
import { useState } from 'react';
import { t } from 'i18next';

const Step1Content = ({
  onNext,
  onPrevious,
}: {
  onNext: () => void;
  onPrevious: () => void;
}) => {
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onNext();
  };

  const handlePrevious = () => {
    onPrevious();
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 px-4">
        {/* TODO: Add QSR Background once available */}
        <Text variant="smallSubheader">Log into your QSR Portal</Text>
        <Text as="p">
          Please go to{' '}
          <Link href="//portal.qsr.cloud" target="_blank">
            portal.qsr.cloud
          </Link>{' '}
          and enter your QSR login credentials to access your QSR account.
        </Text>
        <Footer>
          <Button
            size="large"
            variant="secondary"
            className="flex-1"
            onPress={handlePrevious}
          >
            Back
          </Button>
          <Button
            size="large"
            variant="primary"
            className="flex-1"
            type="submit"
          >
            I've logged in
          </Button>
        </Footer>
      </form>
    </>
  );
};

const Step2Content = ({
  onNext,
  onPrevious,
}: {
  onNext: () => void;
  onPrevious: () => void;
}) => {
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onNext();
  };

  const handlePrevious = () => {
    onPrevious();
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 px-4">
        {/* TODO: Add QSR Background once available */}
        <Text variant="smallSubheader">To locate your SiteUIDs</Text>
        <Text as="p">
          The Site UID is available in QSR Automations Cloud Portal. If you do
          not have access to the Cloud Portal or Site UID, contact your QSR
          Reseller or QSR Support Help Desk for assistance.
        </Text>
        <Text as="p">
          Go to Company &gt; Sites &gt; Select Site &gt; Restaurant Profile &gt;
          SiteUID
        </Text>
        <Footer>
          <Button
            size="large"
            variant="secondary"
            className="flex-1"
            onPress={handlePrevious}
          >
            Back
          </Button>
          <Button
            size="large"
            variant="primary"
            className="flex-1"
            type="submit"
          >
            I'm all set
          </Button>
        </Footer>
      </form>
    </>
  );
};

const Step3Content = ({
  onSubmit,
  onPrevious,
  sites,
  onChange,
}: {
  onSubmit: () => void;
  onPrevious: () => void;
  sites: Array<Site>;
  onChange: (sites: Array<Site>) => void;
}) => {
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSubmit();
  };

  const handlePrevious = () => {
    onPrevious();
  };

  const handleChange = (sites: Site[]) => {
    onChange(sites);
  };

  const isSitesValid =
    sites.length > 0 &&
    sites.every((site) => site.uid.length > 0 && site.name.length > 0);

  return (
    <>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 px-4">
        <Text variant="smallSubheader">Enter your SiteUIDs</Text>
        <Text as="p">
          Add SiteUID with a custom name for every location you would like to
          sync Orders from.{' '}
        </Text>

        <SiteUidTextFields sites={sites} onChange={handleChange} />

        <Footer>
          <Button
            size="large"
            variant="secondary"
            className="flex-1"
            onPress={handlePrevious}
          >
            Back
          </Button>
          <Button
            size="large"
            variant="primary"
            className="flex-1"
            type="submit"
            isDisabled={!isSitesValid}
          >
            Connect with QSR
          </Button>
        </Footer>
      </form>
    </>
  );
};

const QsrSetupWizard = ({
  sites,
  onSitesChange,
  onSubmit,
  onExit,
}: {
  sites: Array<Site>;
  onSubmit: () => void;
  onSitesChange: (sites: Array<Site>) => void;
  onExit: () => void;
}) => {
  const [step, setStep] = useState(1);

  const handleOnNext = () => {
    setStep(step + 1);
  };

  const handleOnPrevious = () => {
    setStep(step - 1);
  };

  const handleSubmit = () => {
    onSubmit();
  };

  return (
    <>
      <Header
        heading={`Connect QSR with ${t('companyName')}`}
        subheading="Follow the steps and complete the integration"
      />
      <QsrProgressStepperContainer selectedStep={step} />
      {step === 1 && <Step1Content onPrevious={onExit} onNext={handleOnNext} />}
      {step === 2 && (
        <Step2Content onPrevious={handleOnPrevious} onNext={handleOnNext} />
      )}
      {step === 3 && (
        <Step3Content
          onPrevious={handleOnPrevious}
          onSubmit={handleSubmit}
          sites={sites}
          onChange={onSitesChange}
        />
      )}
    </>
  );
};

export default QsrSetupWizard;
